import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Crown, Trophy, ExternalLink, Share2, Twitter, Facebook } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShareCard = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share-card/${shareId}/image`;
  const pageUrl = `${window.location.origin}/share/${shareId}`;

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const response = await axios.get(`${API}/share-card/${shareId}/meta`);
        setCardData(response.data);
      } catch (err) {
        console.error('Failed to fetch share card:', err);
        setError('Share card not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCardData();
  }, [shareId]);

  const getPlaceText = (place) => {
    switch(place) {
      case 1: return '1st Place';
      case 2: return '2nd Place';
      case 3: return '3rd Place';
      default: return 'Winner';
    }
  };

  const getPlaceEmoji = (place) => {
    switch(place) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏆';
    }
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(
      `${getPlaceEmoji(cardData?.place)} ${cardData?.winner_name} won ${getPlaceText(cardData?.place)} in "${cardData?.challenge_title}"!\n\n🏆 Prize: ${cardData?.prize}\n\n#Gaming #Winner #CesarMChallenges`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(pageUrl)}`, '_blank');
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="p-8 text-center bg-gray-900 border-red-500/30">
          <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Share Card Not Found</h2>
          <p className="text-gray-400 mb-6">This share card may have expired or been removed.</p>
          <Button onClick={() => navigate('/winners')} className="bg-purple-600 hover:bg-purple-700">
            View All Winners
          </Button>
        </Card>
      </div>
    );
  }

  const title = `${cardData?.winner_name} won ${getPlaceText(cardData?.place)}! - CesarM Challenges`;
  const description = `${getPlaceEmoji(cardData?.place)} Congratulations to ${cardData?.winner_name} for winning ${getPlaceText(cardData?.place)} in "${cardData?.challenge_title}"! Prize: ${cardData?.prize}`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* Header */}
        <header className="py-6 px-4 border-b border-purple-500/20">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white hover:text-purple-400 transition-colors">
              <Trophy className="w-6 h-6 text-red-500" />
              <span className="font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>CesarM Challenges</span>
            </button>
            <Button onClick={() => navigate('/winners')} variant="outline" className="border-purple-500/50 hover:bg-purple-500/20">
              View All Winners
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="py-12 px-4">
          <div className="max-w-2xl mx-auto">
            {/* Winner Card Image */}
            <div className="mb-8 rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-500/30">
              <img 
                src={imageUrl} 
                alt={`${cardData?.winner_name} - ${getPlaceText(cardData?.place)}`}
                className="w-full"
              />
            </div>

            {/* Winner Info */}
            <Card className="p-6 bg-gray-900/80 border-purple-500/30 mb-6">
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">{getPlaceEmoji(cardData?.place)}</div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {cardData?.winner_name}
                </h1>
                <p className="text-purple-400 text-lg">{getPlaceText(cardData?.place)}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-black/40 rounded-xl p-4 text-center">
                  <div className="text-xs text-gray-500 uppercase mb-1">Challenge</div>
                  <div className="text-white font-semibold">{cardData?.challenge_title}</div>
                </div>
                <div className="bg-black/40 rounded-xl p-4 text-center">
                  <div className="text-xs text-gray-500 uppercase mb-1">Prize</div>
                  <div className="text-green-400 font-bold text-lg">{cardData?.prize}</div>
                </div>
              </div>

              {/* Share Buttons */}
              <div className="space-y-3">
                <p className="text-center text-gray-400 text-sm mb-4">Share this victory!</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    onClick={shareToTwitter}
                    className="bg-blue-500 hover:bg-blue-600 flex items-center justify-center gap-2"
                  >
                    <Twitter className="w-5 h-5" />
                    Twitter
                  </Button>
                  <Button 
                    onClick={shareToFacebook}
                    className="bg-blue-700 hover:bg-blue-800 flex items-center justify-center gap-2"
                  >
                    <Facebook className="w-5 h-5" />
                    Facebook
                  </Button>
                </div>
              </div>
            </Card>

            {/* View Count */}
            <p className="text-center text-gray-600 text-sm">
              {cardData?.views} view{cardData?.views !== 1 ? 's' : ''}
            </p>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-purple-500/20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-500 flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4 text-red-500" />
              CesarM Challenges
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ShareCard;
